import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "react-query";
import {
  useNotify,
  SimpleForm,
  TextInput,
  NumberInput,
  useResourceContext,
  required,
  number,
  maxLength,
  useRefresh,
  useDataProvider,
  useUpdate,
  Edit,
  AutocompleteInput,
  ReferenceInput,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import Transition from "../../transition/Transition";

const validateCompany = [required(), maxLength(36)];
const validateTarget = [required(), number()];
const validateStartDate = [required()];
const validateEndDate = (value, allValues) => {
  if (typeof value === "undefined") {
    return "Required"
  } else if (allValues.start_date) {
    if (value > allValues.start_date) {
      return undefined;
    }
    return "Should be greater than StartDate"
  }
};

export default function SalesmanTargetEditDialog({ open, setOpen, recordId }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const dataProvider = useDataProvider();

  // GET COMPANY LISTS
  const {
    data: companies,
    isLoading: isLoadingCompanies,
    error: companyError,
  } = useQuery(["companies/select", "getListNoParams"], () =>
    dataProvider.getListNoParams("companies/select")
  );
  // GET COMPANY LISTS

  const save = useCallback(
    async (values) => {
      try {
        await update(
          resource,
          { id: values.id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [update]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      TransitionComponent={Transition}
    >
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>Edit Salesman Target</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent>
            <TextInput source="id" sx={{ display: "none" }} />

            <AutocompleteInput
              variant="outlined"
              source="company_id"
              isRequired
              choices={isLoadingCompanies ? [] : companies}
              fullWidth
              validate={validateCompany}
            />

            <ReferenceInput
              label="Salesman"
              source="salesman_id"
              reference="salesmen"
            >
              <AutocompleteInput
                variant="outlined"
                optionText="name"
                filterToQuery={(searchText) => ({
                  code: searchText,
                  name: searchText,
                })}
                isRequired
                fullWidth
                validate={validateCompany}
              />
            </ReferenceInput>

            <NumberInput
              variant="outlined"
              source="target"
              fullWidth
              isRequired
              validate={validateTarget}
            />

            <MuiDatePickerInput
              variant="outlined"
              source="start_date"
              fullWidth
              isRequired
              validate={validateStartDate}
              maxDateSource="end_date"
            />
            <MuiDatePickerInput
              variant="outlined"
              source="end_date"
              fullWidth
              isRequired
              validate={validateEndDate}
              minDateSource="start_date"
            />
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
